import { Badge, Button, toast } from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HistoryIcon } from 'lucide-react';
import { useState } from 'react';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog } from '../../components/confirm-dialog.tsx';
import { LoadingRows, Panel } from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { useCan } from '../../lib/session.ts';

const SOURCE: Readonly<Record<string, string>> = {
  USER_STATEMENT: 'Dicho por el cliente',
  DOCUMENT: 'Documento',
  TOOL: 'Herramienta',
  ANALYST: 'Analista',
};

/** Memoria del cliente: hechos con fuente, confianza y vigencia; rollback a checkpoint con motivo. */
export function MemoryPanel({ customerId }: { customerId: string }) {
  const canRollback = useCan('applications:operate');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.memory(customerId),
    queryFn: () => api.getCustomerMemory(customerId),
  });
  const [checkpoint, setCheckpoint] = useState<string | null>(null);
  const rollback = useMutation({
    mutationFn: (reason: string) => api.rollbackMemory(customerId, checkpoint ?? '', reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memory(customerId) });
      toast.success('Memoria restaurada al checkpoint');
    },
  });
  return (
    <Panel
      title="Memoria del cliente"
      description="Lo que el agente recuerda entre sesiones (con fuente y confianza)."
    >
      {query.isPending ? (
        <LoadingRows rows={3} />
      ) : query.isError || query.data.current.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin hechos de memoria.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <ul className="flex flex-col gap-2 text-sm">
            {query.data.current.map((f) => (
              <li key={f.id} className="rounded-lg border border-border px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <code className="font-mono text-xs">{f.key}</code>
                  <Badge
                    variant={
                      f.status === 'VALIDATED'
                        ? 'success'
                        : f.status === 'PROPOSED'
                          ? 'warning'
                          : 'outline'
                    }
                  >
                    {f.status === 'VALIDATED'
                      ? 'Confirmado'
                      : f.status === 'PROPOSED'
                        ? 'Propuesto'
                        : f.status}
                  </Badge>
                </div>
                <p className="font-medium">
                  {typeof f.value === 'string' ? f.value : JSON.stringify(f.value)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {SOURCE[f.sourceType] ?? f.sourceType}
                  {f.sourceRef && ` · ${f.sourceRef}`} · confianza {f.confidence} · desde{' '}
                  {formatDateTime(f.validFrom)}
                </p>
              </li>
            ))}
          </ul>
          {query.data.checkpoints.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Checkpoints</p>
              <ul className="flex flex-col gap-2">
                {query.data.checkpoints.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                    <span>
                      {c.label}
                      <span className="block text-xs text-muted-foreground">
                        {formatDateTime(c.createdAt)}
                      </span>
                    </span>
                    {canRollback && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCheckpoint(c.id);
                        }}
                      >
                        <HistoryIcon /> Rollback
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <ConfirmActionDialog
        open={checkpoint !== null}
        onOpenChange={(o) => {
          if (!o) setCheckpoint(null);
        }}
        title="Restaurar la memoria a un checkpoint"
        description="Los hechos posteriores al checkpoint pasan al historial (no se borran) y el agente deja de usarlos."
        confirmLabel="Restaurar memoria"
        onConfirm={async (reason) => {
          await rollback.mutateAsync(reason);
        }}
      />
    </Panel>
  );
}
